import { useCallback } from 'react';
import { DEFAULT_BUSINESS_AREA } from '@pkgs/components/hooks/stores/search/slices/objectTypeFilterSlice';
import { getSearchResultPageLink } from '@pkgs/components';
import { type SearchListingsFilters, type LocationBreadcrumbItem } from '@pkgs/api';
import { InlineLink } from '@ev-tech/ui-core-components/InlineLink';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { ArrowThinRight } from '@/icons/small/ArrowThinRight';
import { useSettings } from '@/hooks/useSettings';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { useListing } from '@/hooks/queries/useListing';

import styles from './Breadcrumb.module.css';

type ItemsProps = LocationBreadcrumbItem & {
  onClick?: () => void;
};

export interface BreadcrumbProps {
  items: ItemsProps[];
  isIndexed: boolean;
  className?: string;
  'data-testid'?: string;
}

export const Breadcrumb = ({ items, isIndexed, className, 'data-testid': dataTestId }: BreadcrumbProps) => {
  const {
    data: { genericListing },
  } = useListing();
  const { trackExposeNavigationEvent } = useExposeTracking();
  const { countryCode, currency, language, measurementSystem } = useSettings();

  const linkListLength = items.length;

  const handleLinkClick = useCallback(
    (autogenUrl: string, index: number, onClick?: () => void) => {
      trackExposeNavigationEvent({
        targetUrl: autogenUrl,
        elementId: `breadcrumb_${index + 1}_link`,
      });
      onClick?.();
    },
    [trackExposeNavigationEvent],
  );

  return (
    <nav role="navigation" aria-label="Breadcrumb" className={className} data-testid={dataTestId}>
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isLast = index === linkListLength - 1;
          const { autogenUrl, text, placeId, onClick } = item;
          const isSerpLink = !autogenUrl && !!placeId;

          let url = autogenUrl;
          if (isSerpLink && !isIndexed) {
            const params: SearchListingsFilters = {
              placeIds: [placeId],
              businessArea: 'businessArea' in genericListing ? [genericListing.businessArea] : [DEFAULT_BUSINESS_AREA],
              propertyMarketingType: [genericListing.propertyMarketingType],
            };

            url = getSearchResultPageLink({
              filters: params,
              options: { language, currency, countryCode, measurementSystem },
            });
          }

          return (
            <li key={`${index}_${placeId}`} className={styles.item}>
              {url ? (
                <InlineLink as="a" href={url} onClick={() => handleLinkClick(url, index, onClick)} className={styles.link} label={text} />
              ) : (
                <Copy text={text} size="m" />
              )}
              {!isLast && <Copy as="span" className={styles.icon} text={<ArrowThinRight />} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
